/**
 * Freeze V2 — Sub-Grid Info Panel
 *
 * Displays aspiration summary and cane distribution for slaughterhouse
 * sites. Replaces PrimeReact DataTable/Panel with plain HTML + Tailwind.
 */
import { memo, useMemo } from 'react';

import type { LiveEmbryoResponse, TempEmbryoResponse } from '@/services/freeze';

type AspirationRow = {
  category: string;
  d65: number;
  d7: number;
  totals: number;
};

type CaneCount = {
  cane: string;
  count: number;
  grade: number;
};

function buildAspirationData(
  liveEmbryos: LiveEmbryoResponse[],
  tempEmbryos: TempEmbryoResponse[],
): AspirationRow[] {
  let liveD65 = 0,
    liveD7 = 0,
    tempD65 = 0,
    tempD7 = 0;

  for (const e of liveEmbryos) {
    if (e.d65 === 1) liveD65++;
    if (e.d7 === 1) liveD7++;
  }
  for (const e of tempEmbryos) {
    if (e.d65 === 1) tempD65++;
    if (e.d7 === 1) tempD7++;
  }

  return [
    { category: 'Live', d65: liveD65, d7: liveD7, totals: liveD65 + liveD7 },
    { category: 'Temp', d65: tempD65, d7: tempD7, totals: tempD65 + tempD7 },
    {
      category: 'Total',
      d65: liveD65 + tempD65,
      d7: liveD7 + tempD7,
      totals: liveD65 + tempD65 + liveD7 + tempD7,
    },
  ];
}

function buildCaneDistribution(
  liveEmbryos: LiveEmbryoResponse[],
  tempEmbryos: TempEmbryoResponse[],
): { grade1: CaneCount[]; grade2: CaneCount[] } {
  const counts = new Map<string, CaneCount>();

  for (const e of [...liveEmbryos, ...tempEmbryos]) {
    const cane =
      (e as LiveEmbryoResponse).cane ?? (e as TempEmbryoResponse).cane ?? '';
    const grade = ('grade' in e ? (e.grade ?? 1) : 1) as number;
    if (!cane) continue;
    const key = `${cane}-${grade}`;
    const existing = counts.get(key);
    if (existing) {
      existing.count++;
    } else {
      counts.set(key, { cane, count: 1, grade });
    }
  }

  const grade1: CaneCount[] = [];
  const grade2: CaneCount[] = [];
  for (const entry of counts.values()) {
    if (entry.grade <= 1) grade1.push(entry);
    else grade2.push(entry);
  }
  grade1.sort((a, b) => a.cane.localeCompare(b.cane));
  grade2.sort((a, b) => a.cane.localeCompare(b.cane));

  return { grade1, grade2 };
}

type SubGridInfoProps = {
  liveEmbryos: LiveEmbryoResponse[];
  tempEmbryos: TempEmbryoResponse[];
};

export const SubGridInfo = memo(function SubGridInfo({
  liveEmbryos,
  tempEmbryos,
}: SubGridInfoProps) {
  const aspiration = useMemo(
    () => buildAspirationData(liveEmbryos, tempEmbryos),
    [liveEmbryos, tempEmbryos],
  );
  const canes = useMemo(
    () => buildCaneDistribution(liveEmbryos, tempEmbryos),
    [liveEmbryos, tempEmbryos],
  );

  return (
    <div className="flex w-56 flex-col gap-3 text-xs">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-gray-300 text-gray-500">
            <th className="py-1 pr-2"></th>
            <th className="py-1 pr-2">D6.5</th>
            <th className="py-1 pr-2">D7</th>
            <th className="py-1">Totals</th>
          </tr>
        </thead>
        <tbody>
          {aspiration.map((row) => (
            <tr
              key={row.category}
              className={row.category === 'Total' ? 'font-semibold' : ''}
            >
              <td className="py-0.5 pr-2">{row.category}</td>
              <td className="py-0.5 pr-2">{row.d65}</td>
              <td className="py-0.5 pr-2">{row.d7}</td>
              <td className="py-0.5">{row.totals}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="rounded border border-gray-200 bg-white p-2">
        <div className="mb-1 font-semibold text-gray-700">
          Cane Distribution
        </div>
        <div className="flex gap-4">
          <div>
            <div className="font-medium text-gray-600">Grade 1:</div>
            {canes.grade1.length === 0 ? (
              <div className="text-gray-400">—</div>
            ) : (
              canes.grade1.map((c) => (
                <div key={c.cane}>
                  {c.cane}: {c.count}
                </div>
              ))
            )}
          </div>
          <div>
            <div className="font-medium text-gray-600">Grade 2:</div>
            {canes.grade2.length === 0 ? (
              <div className="text-gray-400">—</div>
            ) : (
              canes.grade2.map((c) => (
                <div key={c.cane}>
                  {c.cane}: {c.count}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
